import type { RootState } from "redux/store";
import RequestStatus from "../RequestStatus";
import {
  ClientLens,
  CollectUserView,
  FirmWithLenses,
  PendingCollectUserView,
  PendingUserView,
  SimpleLensView,
  UserInfo,
} from "generated/graphql";
import { createSelector } from "@reduxjs/toolkit";
import { fullNameFromParts } from "helpers/name-helpers";

// Users queries
export const selectUsersStatus = (state: RootState): RequestStatus => state.client.usersStatus;
export const selectUsers = (state: RootState): UserInfo[] => state.client.users;

export const selectActiveUsers = (state: RootState): UserInfo[] =>
  state.client.users.filter((user) => !user.deactivated);
export const selectPendingUsers = (state: RootState): PendingUserView[] => state.client.pendingUsers;
export const selectPendingUsersSorted = createSelector([selectPendingUsers], (pendingUsers) =>
  pendingUsers.slice(0).reverse()
);

export const selectPendingCollectUsers = (state: RootState): PendingCollectUserView[] =>
  state.client.pendingCollectUsers.slice(0).sort((a, b) => (a.lastInvitedAt < b.lastInvitedAt ? 1 : -1));

export const selectUserById = createSelector([selectUsers, (_state, id?: number) => id], (users, userId) =>
  userId !== undefined ? users.find((u) => u.id === userId) : undefined
);
// Collect Users queries
export const selectCollectUsersStatus = (state: RootState): RequestStatus => state.client.collectUsersStatus;
export const selectCollectUsers = (state: RootState): CollectUserView[] => state.client.collectUsers;
export const selectCollectUsersSorted = createSelector([selectCollectUsers], (users) =>
  users
    .slice(0)
    .sort((a, b) =>
      fullNameFromParts(a.firstName, a.lastName)
        .toLowerCase()
        .localeCompare(fullNameFromParts(b.firstName, b.lastName).toLowerCase())
    )
);
// Firms queries
export const selectFirmsWithLenses = (state: RootState): FirmWithLenses[] => state.client.firms;
export const selectFirmsStatus = (state: RootState): RequestStatus => state.client.firmsStatus;
export const selectActiveFirmId = (state: RootState): string => state.client.activeFirmId;
export const selectActiveFirmIdN = createSelector([selectActiveFirmId], (activeFirmId) =>
  activeFirmId ? parseInt(activeFirmId, 10) : undefined
);
// Lenses queries
export const selectLenses = (state: RootState): SimpleLensView[] => state.client.lenses;
export const selectLensesStatus = (state: RootState): RequestStatus => state.client.lensesStatus;
export const selectClientLenses = (state: RootState): { [key: number]: ClientLens } => state.client.clientLenses;
